import { format, isValid } from 'date-fns';
import { MessageDescriptor } from 'react-intl';
import { AmlTemplate, Domanda } from '../api/generated';
import { SurveyRisposte } from '../api/generated/dob';
import {
  FormStrutturaQuestionarioResponseQuestionResponse,
  FormStrutturaQuestionarioResponseSectionResponse,
  FormStrutturaQuestionarioResponseThematicAreaResponse,
  FormThematicAreaResponse,
  FormThematicAreaResponseSectionResponse,
  QuestionnaireMifidResponse,
  QuestionnaireRequestAnswerRequest,
} from '../api/generated/prometeia';
import { env } from '../env';
import { signatureAreaMessages } from '../messages';
import {
  Amount,
  BaseProductCategory,
  ManagedMacroProduct,
  ManagedProductCategory,
  ProductCategory,
  ProductMacroCategoryType,
  SignatureDocumentPracticeType,
  Wallet,
} from '../model';
import { GenericNormalizedModel } from '../model/generic-normalized-model';
import { MifidImage } from '../store/features/home/onboarding-self/home-onboarding-self-mifid-survey-types';

export const isEmptyObject = (obj: Object) => {
  return !obj || !Object.keys(obj)?.some((x) => obj[x] !== void 0);
};

export function array2Map<T>(
  array: T[],
  key?: keyof T
): GenericNormalizedModel<T> {
  const map: GenericNormalizedModel<T> = {
    items: {},
    all: [],
    selected: null,
  };
  return array.reduce((acc, item) => {
    const id = item[key] as any;
    acc.items[id] = item;
    acc.all.push(id);
    return acc;
  }, map);
}

export interface CurrencyFormatterConfig {
  significantDigits?: number;
  thousandsSeparator?: string;
  decimalSeparator?: string;
  currency?: string;
  currencyDisplay?: 'symbol' | 'code';
  showCurrency?: boolean;
  showSpace?: boolean;
  showNd?: boolean;
}

export interface PercentageFormatterConfig
  extends Omit<
    CurrencyFormatterConfig,
    'currency' | 'currencyDisplay' | 'showCurrency'
  > {
  isPercentage?: boolean;
}

const defaultConfigs: CurrencyFormatterConfig = {
  currency: 'EUR',
  significantDigits: 2,
  currencyDisplay: 'symbol',
  thousandsSeparator: '.',
  decimalSeparator: ',',
  showCurrency: true,
  showSpace: true,
  showNd: false,
};
export const dateFormatter = (
  date: Date,
  dateFormat = 'dd/MM/yyyy'
): string => {
  return isValid(date) ? format(date, dateFormat) : '-';
};

export const currencyFormatter = (
  value: number,
  config?: CurrencyFormatterConfig
): string => {
  if (typeof value !== 'number') return '-';

  const {
    currency,
    significantDigits,
    currencyDisplay,
    thousandsSeparator,
    decimalSeparator,
    showCurrency,
    showSpace,
  } = { ...defaultConfigs, ...config };
  if (value === -1) return 'N/D';

  const formattedValue = value.toFixed(significantDigits);

  let formattedCurrency = '';

  if (currencyDisplay === 'symbol') {
    //TODO update with other currecy
    switch (currency) {
      case 'EUR':
        formattedCurrency = '€';
        break;
      default:
        formattedCurrency = currency || '';
    }
  } else if (currencyDisplay === 'code' && currency) {
    formattedCurrency = currency;
  }

  const [integreValue, decimal] = formattedValue.split('.');
  return `${integreValue.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator)}${
    decimal ? decimalSeparator : ''
  }${decimal || ''}${showSpace ? ' ' : ''}${
    showCurrency ? formattedCurrency : ''
  }`;
};

export const amountFormatter = (
  amount: Amount,
  config: Omit<CurrencyFormatterConfig, 'currency'> = {}
): string => {
  return currencyFormatter(amount?.value, {
    ...config,
    currency: amount?.currency,
  });
};

export const numberFormatter = (
  value: number,
  config: Omit<
    CurrencyFormatterConfig,
    'currency' | 'currencyDisplay' | 'showCurrency'
  > = {}
): string => {
  return currencyFormatter(value, {
    ...config,
    showCurrency: false,
  });
};

export const percentageFormatter = (
  value: number,
  config: PercentageFormatterConfig = { isPercentage: false }
): string => {
  if (isNaN(value)) {
    return undefined;
  }
  const valuePercentage = config?.isPercentage ? value * 100 : value;
  const valueFormatted = currencyFormatter(valuePercentage, {
    ...config,
    showCurrency: false,
  });

  return `${valueFormatted}%`;
};

const checkContract = (el: ManagedProductCategory, isin: string) =>
  el.macroProducts?.some((macroProd: ManagedMacroProduct) =>
    macroProd.products?.some((prod) => prod.isin === isin)
  );

export const getProductMacroCategory = (
  wallet: Wallet,
  isin: string
): ProductMacroCategoryType => {
  return wallet.macroCategories.find((item) => {
    if (item.type === 'Managed' || item.type === 'RealEconomy') {
      return item.categories?.some((el: ManagedProductCategory) =>
        checkContract(el, isin)
      );
    }
    return item.categories?.some((el: BaseProductCategory) =>
      el.products?.some((prod) => prod.isin === isin)
    );
  }).type;
};

export const getProductCategory = (
  wallet: Wallet,
  isin: string
): ProductCategory => {
  let category: ProductCategory = null;
  wallet?.macroCategories?.some((item) => {
    if (item.type === 'Managed' || item.type === 'RealEconomy') {
      category = item.categories.find((el: ManagedProductCategory) =>
        checkContract(el, isin)
      );
    } else {
      category = item.categories.find((el: BaseProductCategory) =>
        el.products?.some((prod) => prod.isin === isin)
      );
    }

    return !!category;
  });

  return category;
};

export const getPracticeTitleDescriptor = (
  type?: SignatureDocumentPracticeType
): MessageDescriptor => {
  switch (type) {
    case 'MiFiDQuestionnaire':
      return signatureAreaMessages.mifidPracticeTitle;
    case 'AMLQuestionnaire':
      return signatureAreaMessages.amlPracticeTitle;
    case 'DigitalServiceMembership':
      return signatureAreaMessages.asdPracticeTitle;
    case 'ContactData':
      return signatureAreaMessages.changeContactPracticeTitle;
    case 'ONBOARDING_NUOVO_CLIENTE':
      return signatureAreaMessages.dobPracticeTitle;
    default:
      return signatureAreaMessages.practiceTitle;
  }
};

export const getQuestionsCodeBySection = (
  section: FormStrutturaQuestionarioResponseSectionResponse | undefined
) => {
  return section?.questionList?.map((q) => q.questionCode) || [];
};

export const getSubQuestionsCodeByTableQuestion = (
  question: FormStrutturaQuestionarioResponseQuestionResponse | undefined
) => {
  return question?.questionTableList?.map((q) => q.questionCode) || [];
};

export const getQuestionsCodeAndTypeBySection = (
  section: FormStrutturaQuestionarioResponseSectionResponse | undefined
) => {
  return (
    section?.questionList?.map((q) => {
      return {
        code: q.questionCode,
        type: q.type,
      };
    }) || []
  );
};

export const getPartialAnswer = (
  questionnaire: QuestionnaireMifidResponse | undefined,
  partialCustomerQuestionnaireAnswers: QuestionnaireRequestAnswerRequest[],
  questionCode: string
): QuestionnaireRequestAnswerRequest | undefined => {
  const partial = partialCustomerQuestionnaireAnswers?.find(
    (a) => a.questionCode === questionCode
  );
  if (partial) {
    return partial;
  }
  const fromResponse = getAnswerById(questionnaire, questionCode);
  if (fromResponse && fromResponse.questionCode) {
    return fromResponse as QuestionnaireRequestAnswerRequest;
  }
  return undefined;
};

export const getQuestionByCode = (
  section: FormStrutturaQuestionarioResponseSectionResponse | undefined,
  code: string
) => {
  return section?.questionList?.find((q) => q.questionCode === code);
};

export const getAnswerById = (
  questionnaire: QuestionnaireMifidResponse | undefined,
  questionCode: string
) => {
  return questionnaire?.profiloRischio?.answerList?.find(
    (a) => a?.questionCode === questionCode
  );
};

export const getAmlQuestionById = (
  questionnaire: Domanda[] | undefined,
  questionId: number
) => {
  return questionnaire?.find((domanda) => domanda.idDomanda === questionId);
};

export const getImageByCode = (key: string, images: MifidImage[]) => {
  return images?.find((i) => i.imageKey === key);
};

export const getAnswerDetailsById = (
  answers: QuestionnaireRequestAnswerRequest[],
  questionCode: string | undefined
) => {
  return answers.find((a) => a?.questionCode === questionCode);
};

export const getAllAnswerDetailsById = (
  answers: QuestionnaireRequestAnswerRequest[],
  questionCode: string | undefined
) => {
  if (questionCode === 'PF_ESG_interessi') {
    return answers.filter(
      (a) =>
        a.questionCode === 'PF_ESG_interessi___no' ||
        a.questionCode === 'PF_ESG_interessi___si' ||
        a.questionCode === 'PF_ESG_interessi___ambientale' ||
        a.questionCode === 'PF_ESG_interessi___sociale' ||
        a.questionCode === 'PF_ESG_interessi___governo'
    );
  } else if (
    questionCode === 'PF_FS_fontiReddito' &&
    env.appModule === 'mobile'
  ) {
    return answers.filter((a) => a.questionCode.includes('PF_FS_fontiReddito'));
  } else if (
    questionCode === 'PF_OB_strumentiFinanziari' &&
    env.appModule === 'mobile'
  ) {
    return answers.filter((a) =>
      a.questionCode.includes('PF_OB_strumentiFinanziari')
    );
  } else if (questionCode === 'PF_FA_normativa' && env.appModule === 'mobile') {
    return answers.filter((a) => a.questionCode.includes('PF_FA_normativa'));
  } else {
    return answers.filter((a) => a.questionCode === questionCode);
  }
};

export const getSectionByCode = (
  questionnaire: QuestionnaireMifidResponse | undefined,
  sectionCode: string
) => {
  const areas: FormStrutturaQuestionarioResponseThematicAreaResponse[] =
    questionnaire?.strutturaQuestionario
      ?.map((t) => t?.thematicAreaList)
      ?.flat() || [];
  const sections: FormStrutturaQuestionarioResponseSectionResponse[] =
    areas?.map((a) => a?.sectionList)?.flat() || [];

  return sections?.find((s) => s?.sectionCode === sectionCode);
};

export const getSectionThematicByCode = (
  questionnaire: FormThematicAreaResponse | undefined,
  sectionCode: string
) => {
  const sections: FormThematicAreaResponseSectionResponse[] =
    questionnaire?.thematicAreaList?.map((a) => a?.sectionList)?.flat() || [];

  return sections?.find((s) => s?.sectionCode === sectionCode);
};

export const getMifidQuestionByCode = (
  questionnaire: FormThematicAreaResponse | undefined,
  questionCode: string
): FormStrutturaQuestionarioResponseQuestionResponse | undefined => {
  const sections: FormThematicAreaResponseSectionResponse[] =
    questionnaire?.thematicAreaList?.map((a) => a?.sectionList)?.flat() || [];

  const questions = sections.flatMap((s) => {
    const question = getQuestionByCode(s, questionCode);
    if (question) {
      return question;
    }
    return [] as FormStrutturaQuestionarioResponseQuestionResponse[];
  });

  return questions?.length > 0 ? questions[0] : undefined;
};

export const getAnwserToKeep = (
  answers: QuestionnaireRequestAnswerRequest[],
  questionCode: string | undefined,
  valueToRemove: string
) => {
  return answers
    .filter((a) => a.questionCode === questionCode && a.value !== valueToRemove)
    .reduce<string[]>((prev, curr) => {
      if (curr.value) {
        prev.push(curr.value);
      }
      return prev;
    }, []);
};

export const mapPartialAmlToPostRequest = (
  template: AmlTemplate,
  partial: QuestionnaireRequestAnswerRequest[]
) => {
  const onlyQuestionWithIdRisposta = template.sezioni.flatMap((s) =>
    s.listaDomande.filter((d) => d.listaRisposte.length > 0)
  );

  const domandaMap = new Map<number, Domanda>();

  partial.forEach((p) => {
    const idDomanda = +p.questionCode;
    const isIdRisposta =
      onlyQuestionWithIdRisposta.find(
        (a) => '' + a.idDomanda === p.questionCode
      ) !== undefined;

    const answer = isIdRisposta
      ? { idRisposta: +p.value }
      : { descRisposta: p.value };

    if (domandaMap.has(idDomanda)) {
      const currentDomanda = domandaMap.get(idDomanda);
      const alreadyExists = currentDomanda.listaRisposte.some((r) =>
        isIdRisposta
          ? r.idRisposta === (answer as any).idRisposta
          : r.descRisposta === (answer as any).descRisposta
      );

      if (!alreadyExists) {
        currentDomanda.listaRisposte.push(answer);
      }
    } else {
      domandaMap.set(idDomanda, {
        idDomanda,
        listaRisposte: [answer],
      } as Domanda);
    }
  });

  [68, 69].forEach((id) => {
    if (!domandaMap.has(id)) {
      domandaMap.set(id, {
        idDomanda: id,
        listaRisposte: [{ idRisposta: 85 }],
      } as Domanda);
    }
  });

  return Array.from(domandaMap.values());
};

export const mapAmlResponseToPartial = (response: SurveyRisposte) => {
  const partial = [];
  if (response?.listaDomande?.length)
    for (const d of response?.listaDomande || []) {
      if (d.listaRisposte && d.idDomanda)
        for (const r of d.listaRisposte) {
          partial.push({
            questionCode: '' + d.idDomanda,
            value: r.idRisposta ? '' + r.idRisposta : r.descRisposta,
          });
        }
    }

  return partial;
};

export const someIsMultipleAnswer = (d: Domanda, idDomanda: number) =>
  d.idDomanda === idDomanda &&
  (d.inputType === 'CHECKBOX' ||
    d.inputType === 'RADIO_CHECKBOX_PG' ||
    d.inputType === 'RADIO_CHECKBOX_ESG_PG' ||
    d.inputType === 'RADIO_CHECKBOX_ESG_PD' ||
    d.inputType === 'RADIO_CHECKBOX_PF' ||
    d.inputType === 'RADIO_CHECKBOX_ESG_PF' ||
    d.inputType === 'RADIO_CHECKBOX_2_ESG_PF');

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const emptyFunction = () => {};
